import React, { useState, useContext, useMemo } from 'react';
import './jokeAnalysisComponent.css';
import CurrentGraph from './currentGraph';
import CreateNewArray from './createNewArray';
import LoadingBarPercentageSetupPunchLaughs from './LoadingBarPercentageSetupPunchLaughs';
import AppContext from '../../AppContext';

// Import utility functions for counting words
import {
  countTotalSetupLengthOfPerformance,
  countTotalPunchlineLengthOfPerformance,
  countTotalTaglineLengthOfPerformance
} from '../../utils/analysis/JsonTextAnalysis'; 

const JokeAnalysisComponent = ({ joke, jokeIndex, bitIndex, isExpanded, setIsExpanded, onSaveNewArray }) => {
  const [isRecordingNewArray, setIsRecordingNewArray] = useState(false);
  const { isRecording, setIsRecording } = useContext(AppContext); 

  // Memoize audience response array
  const audienceResponseArray = useMemo(() => {
    const punchlineResponses = joke?.punchline?.response || [];
    const taglineResponses = joke?.taglines
      ? joke.taglines.flatMap(tagline => tagline.punchline?.response || [])
      : [];
  
    return [...punchlineResponses, ...taglineResponses].filter(value => value !== null && value !== undefined);
  }, [joke]);

  // Memoize graph data
  const graphData = useMemo(() => {
    if (audienceResponseArray.length === 0) {
      return [0];
    }
    return audienceResponseArray;
  }, [audienceResponseArray]);

  // Memoize performance analysis calculations
  const performanceAnalysis = useMemo(() => {
    const wordsInSetup = countTotalSetupLengthOfPerformance([joke]);
    const wordsInPunchline = countTotalPunchlineLengthOfPerformance([joke]);
    const wordsInTaglines = countTotalTaglineLengthOfPerformance([joke]);

    const totalWordsInJoke = wordsInSetup + wordsInPunchline + wordsInTaglines;

    const setupTimeLength = audienceResponseArray.filter(value => value === 0).length;
    const laughTimeLength = audienceResponseArray.filter(value => value !== 0).length;
    const jokeTime = setupTimeLength + laughTimeLength;

    const laughScore = audienceResponseArray.reduce((sum, value) => sum + value, 0);
    const maxScore = audienceResponseArray.length * 10;
    const laughQualityScore = ((laughScore / maxScore) * 100).toFixed(1);

    const roundedSetupPercentage = parseFloat(((wordsInSetup / totalWordsInJoke) * 100).toFixed(0));
    const roundedPunchlinePercentage = parseFloat(((wordsInPunchline / totalWordsInJoke) * 100).toFixed(0));
    const roundedTaglinePercentage = parseFloat(((wordsInTaglines / totalWordsInJoke) * 100).toFixed(0));
    const percentageOfLaughterInPerformance = parseFloat(((laughTimeLength / jokeTime) * 100).toFixed(1));

    const formattedSpeakingRate = Math.round((totalWordsInJoke / setupTimeLength) * 60);
    const totalPunchlinesAndTaglines = joke.taglines ? joke.taglines.length + 1 : 1;
    const totalTimeInMinutes = jokeTime / 60;
    const laughsPerMinute = totalTimeInMinutes > 0 ? (totalPunchlinesAndTaglines / totalTimeInMinutes).toFixed(1) : 0;

    const formattedJokeTime = `${Math.floor(jokeTime / 60).toString().padStart(2, '0')}m ${(jokeTime % 60).toString().padStart(2, '0')}s`;

    return {
      totalWordsInJoke,
      wordsInSetup,
      wordsInPunchline,
      wordsInTaglines,
      setupTimeLength,
      laughTimeLength,
      formattedJokeTime,
      roundedSetupPercentage,
      roundedPunchlinePercentage,
      roundedTaglinePercentage,
      percentageOfLaughterInPerformance,
      laughScore,
      laughQualityScore,
      formattedSpeakingRate,
      laughsPerMinute,
    };
  }, [joke, audienceResponseArray]);

  // Ensure hooks are always called and then conditionally render the component
  if (!joke || !isExpanded) return null;

  const handleSaveAndClose = (newArray, selectedOption) => {
    const trimmedArray = [...newArray];
    while (trimmedArray.length > 0 && trimmedArray[trimmedArray.length - 1] === 0) {
      trimmedArray.pop();
    }

    if (onSaveNewArray) {
      onSaveNewArray(bitIndex, jokeIndex, trimmedArray, selectedOption);
    }

    setIsExpanded(true);
    setIsRecordingNewArray(false);
    setIsRecording(false);
  };

  const handleCancelRecording = () => {
    setIsRecordingNewArray(false);
    setIsRecording(false);
  };

  return (
    <div className={`joke-analysis-component-container expanded`}>
      {isExpanded && isRecordingNewArray && (
        <div className="recording-container">
          <CreateNewArray 
            joke={joke}
            onSaveNewArray={handleSaveAndClose}
            onCancelRecording={handleCancelRecording} 
          />
        </div>
      )}

      {isExpanded && !isRecordingNewArray && (
        <div className="joke-analysis">
          <div className="joke-analysis-top">
            <div className="joke-word-analysis"> 
              <p>
                Total Words: <strong>{performanceAnalysis.totalWordsInJoke}</strong> 
                ({performanceAnalysis.wordsInSetup}s / {performanceAnalysis.wordsInPunchline}p
                {performanceAnalysis.wordsInTaglines > 0 ? ` / ${performanceAnalysis.wordsInTaglines}t` : ''})
              </p>
              <p>Speaking Rate: <strong>{performanceAnalysis.formattedSpeakingRate}</strong> WPM</p>
              <p>Laugh Score: <strong>{performanceAnalysis.laughScore}</strong> out of {audienceResponseArray.length * 10}</p>
              <p>Laugh Quality Score: <strong>{performanceAnalysis.laughQualityScore}%</strong></p>
            </div>
            <div className="joke-laugh-analysis">
              <p>Total Joke Time: <strong>{performanceAnalysis.formattedJokeTime}</strong></p>
              <p>Comedian Talking: <strong>{performanceAnalysis.setupTimeLength} sec</strong></p>
              <p>Laughter: <strong>{performanceAnalysis.laughTimeLength} sec</strong></p>
              <p>Laughs/Min: <strong>{performanceAnalysis.laughsPerMinute}</strong></p>
            </div>
          </div>

          <div className="mini-graph-area">
            <div className="loading-bar-container">
              <LoadingBarPercentageSetupPunchLaughs 
                setupPercentage={performanceAnalysis.roundedSetupPercentage}
                punchlinePercentage={performanceAnalysis.roundedPunchlinePercentage}
                taglinePercentage={performanceAnalysis.roundedTaglinePercentage}
                laughPercentage={performanceAnalysis.percentageOfLaughterInPerformance}
              />
            </div>
            <CurrentGraph data={graphData} />
          </div>
        </div>
      )}

      <div className="button-container">
        {!isRecording && (
          <button className="joke-analysis-collapse-button" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? 'Hide' : 'Analyze'}
          </button>
        )}

        {isExpanded && !isRecording && (
          <div className="record-button-container">
            <button className="record-new-array-btn" onClick={() => setIsRecordingNewArray(!isRecordingNewArray)}>
              {isRecordingNewArray ? 'Stats' : 'Record'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default JokeAnalysisComponent;
